<template>
    <div class="paginator container-fluid">
        <div class="row">
            <div class="col-xs-12 col-sm-4">
                <div v-if="number_of_items > smallestNumberPerPage" v-cloak>
                    <label class="m-r-5">{{ page_size_label }}</label>
                    <select v-model="itemsPerPage" class="form-control" :disabled="disabled" @change="changePageSize">
                        <option v-for="size in available_page_sizes" :value="size">{{ size }}</option>
                    </select>
                </div>
            </div>
            <div class="col-xs-12 col-sm-4 text-center text-bold">
                {{ displayedItemsLabel }}
            </div>
            <div class="col-xs-12 col-sm-4 text-right text-bold" v-if="numberOfPages > 1">
                <i v-show="pageNumber > 1" class="fa fa-chevron-left change-page" @click="goToPage(pageNumber - 1)"></i>
                <span v-for="(group, index) in pageSelectors">
                    <span v-for="selector in group"
                        :class="{ 'page-selector': !isNaN(selector), 'inner-selector': index == 1, selected: selector == pageNumber }">
                        <span v-if="!isNaN(selector)" @click="goToPage(selector)">
                            {{ selector }}
                        </span>
                        <span v-else>
                            {{ selector }}
                        </span>
                    </span>
                </span>
                <i v-show="pageNumber < numberOfPages" class="fa fa-chevron-right change-page"
                    @click="goToPage(pageNumber + 1)"></i>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import '../../sass/_global_variables.scss';

.paginator {
    .row>div {
        padding: 0px;
    }


    .form-control {
        width: initial;
        display: inline-block;
    }

    .change-page {
        cursor: pointer;
        color: $med-blue;

        &:hover {
            color: $med-blue-hover;
        }
    }

    .page-selector {
        display: inline-block;
        padding: 0px 5px;

        >span {
            cursor: pointer;
            color: $med-blue;

            &:hover {
                color: $med-blue-hover;
            }
        }

        &.inner-selector:not(:last-child) {
            border-right: 2px solid $divider-gray;
        }

        &.selected>span {
            font-weight: bold;
            color: $body-text;
            cursor: default;

        }
    }
}

@media (max-width: $break-xs) {
    .paginator {
        font-size: $font-lg;

        .row>div {
            text-align: center;

            &:not(:first-child) {
                margin-top: 5px;
            }
        }

        .page-selector {
            padding: 0px 10px;
        }
    }
}
</style>

<script>
export default {
    props: {
        number_of_items: { default: 0 },
        current_page: { default: null },
        current_page_size: { default: null },
        filtered_from: { default: 0 },
        available_page_sizes: { default: function () { return [25, 50, 75, 100]; } },
        initial_page_size: { default: 50 },
        page_size_label: { default: 'Items Per Page:' },
        disabled: { default: false }
    },

    data: function () {
        return {
            itemsPerPage: 0,
            rawPageNumber: 1
        }
    },

    watch: {
        current_page: function (page) {
            this.rawPageNumber = page;
        },

        current_page_size: function (itemsPerPage) {
            this.itemsPerPage = itemsPerPage;
        }
    },

    computed: {
        pageNumber: function () {
            if (this.rawPageNumber < 1) return 1;
            else if (this.rawPageNumber > this.numberOfPages) return this.numberOfPages;
            else return this.rawPageNumber;
        },

        numberOfPages: function () {
            if (this.number_of_items == 0) return 1;
            else {
                return Math.ceil(this.number_of_items / this.itemsPerPage);
            }
        },

        smallestNumberPerPage: function () {
            var val = null;
            for (var i = 0; i < this.available_page_sizes.length; i++) {
                if (!val || this.available_page_sizes[i] < val) {
                    val = this.available_page_sizes[i];
                }
            }
            return val;
        },

        pageSelectors: function () {
            var firstPage;
            var lastPage;
            var numSelectors = 5;

            if (this.pageNumber % numSelectors == 0) {
                firstPage = this.pageNumber - numSelectors + 1;
            }
            else {
                firstPage = Math.floor(this.pageNumber / numSelectors) * numSelectors + 1;
            }
            if (firstPage < 1) firstPage = 1;
            lastPage = firstPage + (numSelectors - 1);
            if (lastPage > this.numberOfPages) lastPage = this.numberOfPages;

            var jumpStartSelectors = [];
            var innerSelectors = [];
            var jumpEndSelectors = [];
            for (var i = firstPage; i <= lastPage; i++) {
                innerSelectors.push(i);
            }

            if (firstPage > 1) {
                jumpStartSelectors.splice(0, 0, 1, '...');
            }
            if (lastPage < this.numberOfPages) {
                jumpEndSelectors.splice(jumpEndSelectors.length, 0, '...', this.numberOfPages);
            }

            return [jumpStartSelectors, innerSelectors, jumpEndSelectors];
        },

        displayedItemsLabel: function () {

            var start = this.pageNumber * this.itemsPerPage - this.itemsPerPage + 1;

            var end = this.pageNumber * this.itemsPerPage;
            if (end > this.number_of_items) end = this.number_of_items;
            if (start > 0) {
                if (start > end) start = end;

                var message = 'Showing ' + start + ' to ' + end + ' of ' + this.number_of_items;
                if (this.filtered_from > this.number_of_items) {
                    message += ' (filtered from ' + this.filtered_from + ')';
                }
                return message;
            }
        }
    },

    methods: {
        changePageSize: function () {
            if (this.disabled) return;
            this.$emit('change', this.pageNumber, this.itemsPerPage);
        },

        goToPage: function (page) {
            if (this.disabled) return;
            var prevPageNumber = this.pageNumber;
            this.rawPageNumber = page;
            if (this.pageNumber != prevPageNumber) {
                this.$emit('change', this.pageNumber, this.itemsPerPage);
            }
        }
    },

    mounted: function () {
        if (this.initial_page_size) this.itemsPerPage = this.initial_page_size;
        else this.itemsPerPage = this.available_page_sizes[0];

        if (this.current_page) {
            this.rawPageNumber = this.current_page;
        }
    }
}
</script>



// WEBPACK FOOTER //
// paginator.vue?94539cd2